<template>
  <div class="ue-students-edit">
    <el-card>
      <div class="header d-flex justify-content-between align-items-center mb-3">
        <h2>Студенты (Год: {{ formData.trancheYear }})</h2>
        <div>
          <el-button @click="goBack" class="ms-2">Назад</el-button>
          <el-button type="primary" @click="saveData" :loading="saving" v-if="formData.status !== 'APPROVED' && hasAccess(editRoles)">Сохранить</el-button>
        </div>
      </div>

      <div class="table-container mb-3" style="overflow-x: auto;" @paste="handlePaste" @keydown="handleKeydown">
        <table class="table table-bordered grid-table">
          <thead>
            <tr>
              <th rowspan="2">Наименование</th>
              <th rowspan="2">Единица измерения</th>
              <th rowspan="2">Количество</th>
              <th rowspan="2">Стоимость за единицу (в месяц), тенге</th>
              <th rowspan="2">Всего</th>
              <th colspan="12" style="padding: 0;">в т.ч. по месяцам</th>
              <th rowspan="2" v-if="formData.status !== 'APPROVED' && hasAccess(editRoles)"></th>
            </tr>
            <tr>
              <th>янв</th>
              <th>фев</th>
              <th>мар</th>
              <th>апр</th>
              <th>май</th>
              <th>июн</th>
              <th>июл</th>
              <th>авг</th>
              <th>сен</th>
              <th>окт</th>
              <th>ноя</th>
              <th>дек</th>
            </tr>
          </thead>
          <tbody>
            <tr class="table-success fw-bold">
              <td colspan="4" class="text-end text-uppercase">ВСЕГО:</td>
              <td class="text-center align-middle">{{ formatNumber(getTotalSum('calculatedSum')) }}</td>
              <td class="text-center align-middle">{{ formatNumber(getTotalSum('janSum')) }}</td>
              <td class="text-center align-middle">{{ formatNumber(getTotalSum('febSum')) }}</td>
              <td class="text-center align-middle">{{ formatNumber(getTotalSum('marSum')) }}</td>
              <td class="text-center align-middle">{{ formatNumber(getTotalSum('aprSum')) }}</td>
              <td class="text-center align-middle">{{ formatNumber(getTotalSum('maySum')) }}</td>
              <td class="text-center align-middle">{{ formatNumber(getTotalSum('junSum')) }}</td>
              <td class="text-center align-middle">{{ formatNumber(getTotalSum('julSum')) }}</td>
              <td class="text-center align-middle">{{ formatNumber(getTotalSum('augSum')) }}</td>
              <td class="text-center align-middle">{{ formatNumber(getTotalSum('sepSum')) }}</td>
              <td class="text-center align-middle">{{ formatNumber(getTotalSum('octSum')) }}</td>
              <td class="text-center align-middle">{{ formatNumber(getTotalSum('novSum')) }}</td>
              <td class="text-center align-middle">{{ formatNumber(getTotalSum('decSum')) }}</td>
              <td v-if="formData.status !== 'APPROVED' && hasAccess(editRoles)"></td>
            </tr>
          </tbody>
          <tbody v-for="(group, gIndex) in formData.groups" :key="group.uuid || gIndex">
            <!-- Group Header Row -->
            <tr class="table-success fw-bold">
              <td colspan="4">
                <div class="d-flex align-items-center">
                  <el-select v-model="group.groupName" placeholder="Выберите категорию" size="small" style="width: 500px" filterable allow-create default-first-option>
                    <el-option label="РАСХОДЫ ПО СТУДЕНТАМ" value="РАСХОДЫ ПО СТУДЕНТАМ"></el-option>
                  </el-select>
                </div>
              </td>
              <td>{{ formatNumber(group.calculatedSum) }}</td>
              <td class="text-center align-middle">{{ formatNumber(getGroupMonthSum(group, 'janSum')) }}</td>
              <td class="text-center align-middle">{{ formatNumber(getGroupMonthSum(group, 'febSum')) }}</td>
              <td class="text-center align-middle">{{ formatNumber(getGroupMonthSum(group, 'marSum')) }}</td>
              <td class="text-center align-middle">{{ formatNumber(getGroupMonthSum(group, 'aprSum')) }}</td>
              <td class="text-center align-middle">{{ formatNumber(getGroupMonthSum(group, 'maySum')) }}</td>
              <td class="text-center align-middle">{{ formatNumber(getGroupMonthSum(group, 'junSum')) }}</td>
              <td class="text-center align-middle">{{ formatNumber(getGroupMonthSum(group, 'julSum')) }}</td>
              <td class="text-center align-middle">{{ formatNumber(getGroupMonthSum(group, 'augSum')) }}</td>
              <td class="text-center align-middle">{{ formatNumber(getGroupMonthSum(group, 'sepSum')) }}</td>
              <td class="text-center align-middle">{{ formatNumber(getGroupMonthSum(group, 'octSum')) }}</td>
              <td class="text-center align-middle">{{ formatNumber(getGroupMonthSum(group, 'novSum')) }}</td>
              <td class="text-center align-middle">{{ formatNumber(getGroupMonthSum(group, 'decSum')) }}</td>
              <td v-if="formData.status !== 'APPROVED' && hasAccess(editRoles)">
                <el-button type="danger" size="small" @click="removeGroup(gIndex)">
                  <font-awesome-icon :icon="['fas', 'trash']" />
                </el-button>
              </td>
            </tr>

            <tr v-for="(item, index) in group.details" :key="item.uuid || index">
              <td>
                <el-select v-model="item.dictStudentExpenseTypeId" @change="(val) => { let obj = dictStudentExpenseTypes.find(x => x.id === val); item.expenseName = obj ? obj.nameRu : ''; }" placeholder="Выберите" size="small" style="width: 100%" filterable default-first-option>
                  <el-option v-for="pi in dictStudentExpenseTypes" :key="pi.id" :label="pi.nameRu" :value="pi.id"></el-option>
                </el-select>
              </td>
              <td>
                <select class="form-select form-select-sm" v-model="item.dictMeasureUnitId">
                  <option v-for="mu in dictMeasureUnits" :key="mu.id" :value="mu.id">{{ mu.nameRu }}</option>
                </select>
              </td>
              <td :data-group-idx="gIndex" :data-item-idx="index" data-col="0"><el-input v-model="item.count" size="small" :formatter="formatInputNumber" :parser="parseInputNumber" @input="recalcCostPerUnit(item)" /></td>
              <td class="text-center align-middle" style="background-color: #e6f4ea; color: var(--el-text-color-regular);">{{ formatNumber(item.costPerUnitPerMonth) }}</td>
              <td class="text-center align-middle" style="background-color: #e6f4ea; color: var(--el-text-color-regular);">{{ formatNumber(item.calculatedSum) }}</td>
              <td :data-group-idx="gIndex" :data-item-idx="index" data-col="1"><el-input v-model="item.janSum" size="small" :formatter="formatInputNumber" :parser="parseInputNumber" @input="recalcTotal(item, group)" /></td>
              <td :data-group-idx="gIndex" :data-item-idx="index" data-col="2"><el-input v-model="item.febSum" size="small" :formatter="formatInputNumber" :parser="parseInputNumber" @input="recalcTotal(item, group)" /></td>
              <td :data-group-idx="gIndex" :data-item-idx="index" data-col="3"><el-input v-model="item.marSum" size="small" :formatter="formatInputNumber" :parser="parseInputNumber" @input="recalcTotal(item, group)" /></td>
              <td :data-group-idx="gIndex" :data-item-idx="index" data-col="4"><el-input v-model="item.aprSum" size="small" :formatter="formatInputNumber" :parser="parseInputNumber" @input="recalcTotal(item, group)" /></td>
              <td :data-group-idx="gIndex" :data-item-idx="index" data-col="5"><el-input v-model="item.maySum" size="small" :formatter="formatInputNumber" :parser="parseInputNumber" @input="recalcTotal(item, group)" /></td>
              <td :data-group-idx="gIndex" :data-item-idx="index" data-col="6"><el-input v-model="item.junSum" size="small" :formatter="formatInputNumber" :parser="parseInputNumber" @input="recalcTotal(item, group)" /></td>
              <td :data-group-idx="gIndex" :data-item-idx="index" data-col="7"><el-input v-model="item.julSum" size="small" :formatter="formatInputNumber" :parser="parseInputNumber" @input="recalcTotal(item, group)" /></td>
              <td :data-group-idx="gIndex" :data-item-idx="index" data-col="8"><el-input v-model="item.augSum" size="small" :formatter="formatInputNumber" :parser="parseInputNumber" @input="recalcTotal(item, group)" /></td>
              <td :data-group-idx="gIndex" :data-item-idx="index" data-col="9"><el-input v-model="item.sepSum" size="small" :formatter="formatInputNumber" :parser="parseInputNumber" @input="recalcTotal(item, group)" /></td>
              <td :data-group-idx="gIndex" :data-item-idx="index" data-col="10"><el-input v-model="item.octSum" size="small" :formatter="formatInputNumber" :parser="parseInputNumber" @input="recalcTotal(item, group)" /></td>
              <td :data-group-idx="gIndex" :data-item-idx="index" data-col="11"><el-input v-model="item.novSum" size="small" :formatter="formatInputNumber" :parser="parseInputNumber" @input="recalcTotal(item, group)" /></td>
              <td :data-group-idx="gIndex" :data-item-idx="index" data-col="12"><el-input v-model="item.decSum" size="small" :formatter="formatInputNumber" :parser="parseInputNumber" @input="recalcTotal(item, group)" /></td>
              <td v-if="formData.status !== 'APPROVED' && hasAccess(editRoles)">
                <el-button type="danger" size="small" @click="removeRow(group, index)">
                  <font-awesome-icon :icon="['fas', 'trash']" />
                </el-button>
              </td>
            </tr>
            
            <tr v-if="formData.status !== 'APPROVED' && hasAccess(editRoles)">
              <td :colspan="hasAccess(editRoles) ? 18 : 17">
                <el-button type="success" size="small" @click="addRow(group)">
                  <font-awesome-icon :icon="['fas', 'plus']"/> Добавить расход
                </el-button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="mb-3" v-if="formData.status !== 'APPROVED' && hasAccess(editRoles)">
        <el-button type="primary" size="small" @click="addGroup">
          <font-awesome-icon :icon="['fas', 'folder-plus']"/> Добавить категорию
        </el-button>
      </div>

    </el-card>
    <vue-loading :active.sync="isLoading" :is-full-page="true"></vue-loading>
  </div>
</template>

<script>
import VueLoading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/css/index.css';
import {showErrorNotify, showSuccessNotify} from "@/utils/notificationUtils";
import ueStudentsService from "@/services/ueStudentsService";
import dictionaryService from "@/services/dictionaryService";
import authService from "@/services/authService";
import {handleGridKeydown} from "@/utils/gridKeyboardNav";

export default {
  name: "UeStudentsEditView",
  components: { VueLoading },
  data() {
    return {
      isLoading: false,
      saving: false,
      uuid: this.$route.params.uuid,
      formData: {
        groups: []
      },
      dictStudentExpenseTypes: [],
      dictMeasureUnits: [],
      editRoles: ['ROLE_VO_UNIVERSITY', 'ROLE_VO_ADMIN', 'ROLE_VO_MANAGER']
    };
  },
  mounted() {
    this.loadDictionaries();
    this.loadData();
  },
  methods: {
    hasAccess(roles) {
      return authService.hasAccess(roles);
    },

    formatNumber(val) {
      if (val === undefined || val === null || val === '' || isNaN(val)) return '0,00';
      const formatted = parseFloat(val).toFixed(2);
      const parts = formatted.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
      return parts.join(',');
    },
    formatInputNumber(value) {
      if (value === undefined || value === null || value === '') return '';
      const parts = value.toString().split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
      return parts.join(',');
    },
    parseInputNumber(value) {
      if (!value) return '';
      let parsed = value.toString().replace(',', '.');
      parsed = parsed.replace(/[^\d.]/g, '');
      const parts = parsed.split('.');
      if (parts.length > 2) {
        parsed = parts[0] + '.' + parts.slice(1).join('');
      }
      return parsed;
    },
    async loadDictionaries() {
      try {
        const [products, items] = await Promise.all([
          dictionaryService.getStudentExpenseTypes(),
          dictionaryService.getMeasureUnits()
        ]);
        this.dictStudentExpenseTypes = products;
        this.dictMeasureUnits = items;
      } catch (e) {
        showErrorNotify('Ошибка загрузки справочников');
      }
    },
    async loadData() {
      if (!this.uuid) return;
      this.isLoading = true;
      try {
        const data = await ueStudentsService.getById(this.uuid);
        if (data) {
          if (!data.groups) data.groups = [];
          this.recalculateAll(data);
          this.formData = data;
        }
      } catch (e) {
        showErrorNotify('Ошибка при загрузке данных');
      } finally {
        this.isLoading = false;
      }
    },
    recalculateAll(data) {
      if (data && data.groups) {
        data.groups.forEach(group => {
          if (group.details) {
            group.details.forEach(item => {
              this.recalcTotal(item, group);
            });
          }
        });
      }
    },
    recalcTotal(item, group) {
      const months = ["janSum", "febSum", "marSum", "aprSum", "maySum", "junSum", "julSum", "augSum", "sepSum", "octSum", "novSum", "decSum"];
      const sum = months.reduce((acc, m) => acc + (parseFloat(item[m]) || 0), 0);
      item.calculatedSum = sum.toFixed(2);
      this.recalcCostPerUnit(item);
      
      if (group) {
        group.calculatedSum = group.details.reduce((s, d) => s + (parseFloat(d.calculatedSum) || 0), 0).toFixed(2);
      }
    },
    recalcCostPerUnit(item) {
      const count = parseFloat(item.count) || 0;
      const totalSum = parseFloat(item.calculatedSum) || 0;
      if (count > 0) {
        item.costPerUnitPerMonth = (totalSum / count / 12).toFixed(2);
      } else {
        item.costPerUnitPerMonth = '0.00';
      }
    },
    getGroupMonthSum(group, monthName) {
      if (!group || !group.details) return 0;
      return group.details.reduce((s, d) => s + (parseFloat(d[monthName]) || 0), 0).toFixed(2);
    },
    getTotalSum(field) {
      if (!this.formData || !this.formData.groups) return 0;
      return this.formData.groups.reduce((acc, group) => {
        if (!group.details) return acc;
        const groupSum = group.details.reduce((sum, item) => sum + (parseFloat(item[field]) || 0), 0);
        return acc + groupSum;
      }, 0).toFixed(2);
    },

    addGroup() {
      this.formData.groups.push({
        groupName: '',
        calculatedSum: 0,
        details: []
      });
    },
    removeGroup(index) {
      if (confirm('Удалить группу и все её строки?')) {
        this.formData.groups.splice(index, 1);
      }
    },
    addRow(group) {
      if (!group.details) group.details = [];
      group.details.push({
        dictStudentExpenseTypeId: null,
        expenseName: '',
        periodicity: '',
        count: '',
        costPerUnitPerMonth: '',
        calculatedSum: '',
        actualSum: '',
        janSum: '', febSum: '', marSum: '', aprSum: '',
        maySum: '', junSum: '', julSum: '', augSum: '',
        sepSum: '', octSum: '', novSum: '', decSum: ''
      });
    },
    removeRow(group, index) {
      if (confirm('Удалить строку?')) {
        group.details.splice(index, 1);
        group.calculatedSum = group.details.reduce((s, d) => s + (parseFloat(d.calculatedSum) || 0), 0).toFixed(2);
      }
    },
    async saveData() {
      this.saving = true;
      try {
        const result = await ueStudentsService.save(this.formData);
        this.recalculateAll(result);
        this.formData = result;
        showSuccessNotify('Данные успешно сохранены');
      } catch (e) {
        showErrorNotify('Ошибка при сохранении');
      } finally {
        this.saving = false;
      }
    },
    goBack() {
      this.$router.push('/expenses/students');
    },
    handleKeydown(event) {
      handleGridKeydown(event, this.$el);
    },
    handlePaste(event) {
      const target = event.target;
      if (!target || target.tagName !== 'INPUT') return;

      const td = target.closest('td');
      if (!td) return;

      const startGroupIdx = parseInt(td.dataset.groupIdx);
      const startItemIdx = parseInt(td.dataset.itemIdx);
      const startCol = parseInt(td.dataset.col);

      if (isNaN(startGroupIdx) || isNaN(startItemIdx) || isNaN(startCol)) return;

      const clipboardData = event.clipboardData || window.clipboardData;
      if (!clipboardData) return;

      const text = clipboardData.getData('text/plain');
      if (!text) return;

      const rows = text.split(/\r?\n/).filter(r => r.trim() !== '');
      if (rows.length <= 1 && !rows[0].includes('\t')) return;

      event.preventDefault();

      const COL_FIELDS = [
        'count',
        'janSum', 'febSum', 'marSum', 'aprSum', 'maySum', 'junSum',
        'julSum', 'augSum', 'sepSum', 'octSum', 'novSum', 'decSum'
      ];

      let currentGroupIdx = startGroupIdx;
      let currentItemIdx = startItemIdx;

      for (let r = 0; r < rows.length; r++) {
        const cols = rows[r].split('\t');
        
        let currentGroup = this.formData.groups[currentGroupIdx];
        if (!currentGroup) break;

        let currentItem = currentGroup.details[currentItemIdx];
        
        // Move to next group if needed
        while (!currentItem) {
          currentGroupIdx++;
          currentItemIdx = 0;
          currentGroup = this.formData.groups[currentGroupIdx];
          if (!currentGroup) break;
          currentItem = currentGroup.details[currentItemIdx];
        }
        
        if (!currentItem) break;

        for (let c = 0; c < cols.length; c++) {
          const colIdx = startCol + c;
          if (colIdx >= COL_FIELDS.length) break;

          const field = COL_FIELDS[colIdx];
          const rawVal = String(cols[c]).replace(/\s/g, '').replace(',', '.');
          const num = parseFloat(rawVal);
          currentItem[field] = isNaN(num) || num === 0 ? '' : num;
        }

        this.recalcTotal(currentItem, currentGroup);
        currentItemIdx++;
      }

      this.$forceUpdate();
    }
  }
};
</script>

<style scoped>
.grid-table th { 
  font-size: 13px; 
  vertical-align: middle; 
  text-align: center; 
}
.grid-table td { 
  font-size: 13px;
  vertical-align: middle; 
}
.calculated-row {
  background-color: #e8f5e9 !important;
}
:deep(.el-input__inner) {
  font-size: 13px;
}
:deep(.el-select .el-input__inner) {
  font-size: 13px;
}
</style>
